// Email Templates Tab Component for AdminPage
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Mail, FileText, Paperclip, Tag, Sparkles, Wand2, Loader2, X, RefreshCw, Save, AlertCircle, MessageSquare, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { templateTags } from './constants';
import * as api from '../../services/api';
import { toast } from 'sonner';

const TemplatesTab = ({ emailTemplates, setEmailTemplates, currentUser }) => {
  const [activeSubTab, setActiveSubTab] = useState('email');
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', body: '', category: '', attachments: [] });
  const [templateAttachments, setTemplateAttachments] = useState([]);
  const [editAttachments, setEditAttachments] = useState([]);
  
  // Message Templates state
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [showAddMessageModal, setShowAddMessageModal] = useState(false);
  const [showEditMessageModal, setShowEditMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [newMessage, setNewMessage] = useState({ name: '', content: '', category: '', channel: 'whatsapp' });
  const [copiedId, setCopiedId] = useState(null);
  
  // Load message templates on mount
  useEffect(() => {
    loadMessageTemplates();
  }, []);

  const loadMessageTemplates = async () => {
    try {
      const res = await api.default.get('/message-templates');
      setMessageTemplates(res.data || []);
    } catch (error) {
      console.error('Error loading message templates:', error);
    }
  };
  
  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [assistantForm, setAssistantForm] = useState({
    template_type: 'seguimiento',
    purpose: '',
    tone: 'profesional',
    target_audience: '',
    key_points: []
  });
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [newKeyPoint, setNewKeyPoint] = useState('');

  const handleAddTemplate = async () => {
    try {
      const templateData = { ...newTemplate, attachments: templateAttachments };
      const response = await api.createEmailTemplate(templateData, currentUser?.email || 'admin@infinito.com');
      setEmailTemplates([...emailTemplates, response.data]);
      setShowAddTemplateModal(false);
      setNewTemplate({ name: '', subject: '', body: '', category: '', attachments: [] });
      setTemplateAttachments([]);
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Error al crear plantilla');
    }
  };

  const handleEditTemplate = async () => {
    try {
      const updateData = { ...selectedTemplate, attachments: editAttachments };
      const response = await api.updateEmailTemplate(selectedTemplate.id, updateData);
      setEmailTemplates(emailTemplates.map(t => t.id === selectedTemplate.id ? response.data : t));
      setShowEditTemplateModal(false);
      setSelectedTemplate(null);
      setEditAttachments([]);
    } catch (error) {
      console.error('Error updating template:', error);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta plantilla?')) {
      try {
        await api.deleteEmailTemplate(id);
        setEmailTemplates(emailTemplates.filter(t => t.id !== id));
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const insertTag = (tag, isEdit = false) => {
    if (isEdit && selectedTemplate) {
      const textarea = document.getElementById('edit-template-body');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = selectedTemplate.body;
        const newText = text.substring(0, start) + tag + text.substring(end);
        setSelectedTemplate({ ...selectedTemplate, body: newText });
      }
    } else {
      const textarea = document.getElementById('new-template-body');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = newTemplate.body;
        const newText = text.substring(0, start) + tag + text.substring(end);
        setNewTemplate({ ...newTemplate, body: newText });
      }
    }
  };

  const handleFileUpload = async (e, isEdit = false) => {
    const files = Array.from(e.target.files);
    
    // Convert files to base64 for storage
    const newAttachments = await Promise.all(files.map(async (file) => {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      return {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        filename: file.name,
        file_data: base64, // Store as base64
        file_size: file.size,
        content_type: file.type
      };
    }));
    
    if (isEdit) {
      setEditAttachments([...editAttachments, ...newAttachments]);
    } else {
      setTemplateAttachments([...templateAttachments, ...newAttachments]);
    }
  };

  const removeAttachment = (id, isEdit = false) => {
    if (isEdit) {
      setEditAttachments(editAttachments.filter(a => a.id !== id));
    } else {
      setTemplateAttachments(templateAttachments.filter(a => a.id !== id));
    }
  };

  const openEditTemplateModal = (template) => {
    setSelectedTemplate(template);
    setEditAttachments(template.attachments || []);
    setShowEditTemplateModal(true);
  };

  // AI Assistant functions
  const openAIAssistant = () => {
    setAssistantForm({
      template_type: 'seguimiento',
      purpose: '',
      tone: 'profesional',
      target_audience: '',
      key_points: []
    });
    setGeneratedTemplate(null);
    setShowAIAssistant(true);
  };

  const handleGenerateTemplate = async () => {
    if (!assistantForm.purpose.trim()) {
      toast.error('Describe el propósito del template');
      return;
    }
    
    setGeneratingTemplate(true);
    try {
      const response = await api.default.post('/agents/assist-template-creation', assistantForm);
      const result = response.data || response;
      
      if (result.success) {
        setGeneratedTemplate(result.generated_template);
        toast.success('¡Template generado exitosamente!');
      } else {
        toast.error(`Error: ${result.error || 'Error desconocido'}`);
      }
    } catch (error) {
      toast.error(`Error al generar template: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const handleSaveGeneratedTemplate = async () => {
    if (!generatedTemplate) return;
    
    try {
      const templateData = {
        name: generatedTemplate.name,
        subject: generatedTemplate.subject,
        body: generatedTemplate.body,
        category: generatedTemplate.category || 'general',
        attachments: []
      };
      
      const response = await api.createEmailTemplate(templateData, currentUser?.email || 'AI Assistant');
      setEmailTemplates([...emailTemplates, response.data]);
      setShowAIAssistant(false);
      setGeneratedTemplate(null);
      toast.success('¡Template guardado exitosamente!');
    } catch (error) {
      toast.error(`Error al guardar: ${error.response?.data?.detail || error.message}`);
    }
  };

  const addKeyPoint = () => {
    if (newKeyPoint.trim()) {
      setAssistantForm(prev => ({
        ...prev,
        key_points: [...prev.key_points, newKeyPoint.trim()]
      }));
      setNewKeyPoint('');
    }
  };

  const removeKeyPoint = (index) => {
    setAssistantForm(prev => ({
      ...prev,
      key_points: prev.key_points.filter((_, i) => i !== index)
    }));
  };

  // ============= MESSAGE TEMPLATES FUNCTIONS =============
  const handleAddMessage = async () => {
    try {
      const messageData = {
        ...newMessage,
        created_by: currentUser?.name || currentUser?.email || 'admin'
      };
      const response = await api.default.post('/message-templates', messageData);
      setMessageTemplates([...messageTemplates, response.data]);
      setShowAddMessageModal(false);
      setNewMessage({ name: '', content: '', category: '', channel: 'whatsapp' });
      toast.success('Plantilla de mensaje creada');
    } catch (error) {
      console.error('Error creating message template:', error);
      toast.error('Error al crear plantilla');
    }
  };

  const handleEditMessage = async () => {
    try {
      const response = await api.default.put(`/message-templates/${selectedMessage.id}`, selectedMessage);
      setMessageTemplates(messageTemplates.map(m => m.id === selectedMessage.id ? response.data : m));
      setShowEditMessageModal(false);
      setSelectedMessage(null);
      toast.success('Plantilla actualizada');
    } catch (error) {
      console.error('Error updating message template:', error);
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta plantilla?')) {
      try {
        await api.default.delete(`/message-templates/${id}`);
        setMessageTemplates(messageTemplates.filter(m => m.id !== id));
        toast.success('Plantilla eliminada');
      } catch (error) {
        console.error('Error deleting message template:', error);
        toast.error('Error al eliminar');
      }
    }
  };

  const copyToClipboard = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const insertMessageTag = (tag) => {
    const textarea = document.getElementById('message-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = showEditMessageModal ? selectedMessage.content : newMessage.content;
      const newText = text.substring(0, start) + tag + text.substring(end);
      if (showEditMessageModal) {
        setSelectedMessage({ ...selectedMessage, content: newText });
      } else {
        setNewMessage({ ...newMessage, content: newText });
      }
    }
  };

  // Group tags by category
  const tagsByCategory = templateTags.reduce((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {});

  // Message variable tags
  const messageVars = [
    { tag: '{{nombre_empresa}}', label: 'Nombre Empresa' },
    { tag: '{{nombre_contacto}}', label: 'Nombre Contacto' },
    { tag: '{{mi_nombre}}', label: 'Mi Nombre' },
    { tag: '{{fecha}}', label: 'Fecha' },
    { tag: '{{hora}}', label: 'Hora' },
    { tag: '{{telefono}}', label: 'Teléfono' },
  ];

  return (
    <>
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail size={16} />
            Plantillas de Email
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="message-templates-tab">
            <MessageSquare size={16} />
            Plantillas de Mensajes
          </TabsTrigger>
        </TabsList>

        {/* EMAIL TEMPLATES TAB */}
        <TabsContent value="email">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-700">Plantillas de Email</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Crea y gestiona plantillas reutilizables para emails</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={openAIAssistant} variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="ai-template-btn">
                  <Sparkles size={18} className="mr-2" />
                  Crear con AI
                </Button>
                <Button onClick={() => setShowAddTemplateModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]" data-testid="add-template-btn">
                  <Plus size={18} className="mr-2" />
                  Nueva Plantilla
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {emailTemplates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {emailTemplates.map((template) => (
                    <div key={template.id} className="p-4 border rounded-lg hover:border-[#D4A843] transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText size={18} className="text-[#D4A843]" />
                          <h5 className="font-medium text-gray-800">{template.name}</h5>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEditTemplateModal(template)} className="p-1 hover:bg-gray-100 rounded">
                            <Edit size={14} className="text-gray-500" />
                          </button>
                          <button onClick={() => handleDeleteTemplate(template.id)} className="p-1 hover:bg-red-50 rounded">
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                      {template.category && (
                        <Badge variant="outline" className="mb-2 text-xs">{template.category}</Badge>
                      )}
                      <p className="text-sm text-gray-600 mb-2 font-medium">{template.subject}</p>
                      <p className="text-xs text-gray-400 line-clamp-2">{template.body}</p>
                      {template.attachments?.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                          <Paperclip size={12} />
                          {template.attachments.length} archivo(s)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Mail size={48} className="mx-auto text-gray-300 mb-4" />
                  <p>No hay plantillas de email creadas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MESSAGE TEMPLATES TAB */}
        <TabsContent value="messages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-700">Plantillas de Mensajes</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Mensajes predefinidos para WhatsApp, SMS y otros canales. Disponibles para todos los usuarios y agentes.</p>
              </div>
              <Button onClick={() => setShowAddMessageModal(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-message-template-btn">
                <Plus size={18} className="mr-2" />
                Nueva Plantilla
              </Button>
            </CardHeader>
            <CardContent>
              {messageTemplates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {messageTemplates.map((message) => (
                    <div key={message.id} className="p-4 border rounded-lg hover:border-green-400 transition-colors bg-white">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare size={18} className={message.channel === 'whatsapp' ? 'text-green-500' : 'text-blue-500'} />
                          <h5 className="font-medium text-gray-800">{message.name}</h5>
                        </div>
                        <div className="flex gap-1">
                          <button 
                            onClick={() => copyToClipboard(message.content, message.id)} 
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Copiar mensaje"
                          >
                            {copiedId === message.id ? <Check size={14} className="text-green-500" /> : <Copy size={14} className="text-gray-500" />}
                          </button>
                          <button onClick={() => { setSelectedMessage(message); setShowEditMessageModal(true); }} className="p-1 hover:bg-gray-100 rounded">
                            <Edit size={14} className="text-gray-500" />
                          </button>
                          <button onClick={() => handleDeleteMessage(message.id)} className="p-1 hover:bg-red-50 rounded">
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2 mb-2">
                        <Badge variant="outline" className="text-xs capitalize">{message.channel || 'whatsapp'}</Badge>
                        {message.category && <Badge variant="secondary" className="text-xs">{message.category}</Badge>}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-3 whitespace-pre-wrap">{message.content}</p>
                      <p className="text-xs text-gray-400 mt-2">Por: {message.created_by}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                  <p>No hay plantillas de mensajes creadas</p>
                  <p className="text-sm mt-2">Crea plantillas para WhatsApp, SMS o mensajes generales</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Template Modal */}
      <Dialog open={showAddTemplateModal} onOpenChange={setShowAddTemplateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Plantilla de Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nombre</label>
                <Input value={newTemplate.name} onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})} placeholder="Ej: Bienvenida Cliente" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Categoría</label>
                <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate({...newTemplate, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bienvenida">Bienvenida</SelectItem>
                    <SelectItem value="seguimiento">Seguimiento</SelectItem>
                    <SelectItem value="propuesta">Propuesta</SelectItem>
                    <SelectItem value="agradecimiento">Agradecimiento</SelectItem>
                    <SelectItem value="promocion">Promoción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Asunto</label>
              <Input value={newTemplate.subject} onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})} placeholder="Asunto del email" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Variables disponibles</label>
              <div className="p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                {Object.entries(tagsByCategory).map(([category, tags]) => (
                  <div key={category} className="mb-2">
                    <p className="text-xs font-medium text-gray-500 mb-1 capitalize">{category}</p>
                    <div className="flex flex-wrap gap-1">
                      {tags.map((t) => (
                        <button key={t.tag} onClick={() => insertTag(t.tag, false)} className="px-2 py-1 text-xs bg-white border rounded hover:bg-[#D4A843] hover:text-white transition-colors" title={t.label}>
                          <Tag size={10} className="inline mr-1" />{t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Cuerpo del Email</label>
              <textarea id="new-template-body" value={newTemplate.body} onChange={(e) => setNewTemplate({...newTemplate, body: e.target.value})} className="w-full h-40 p-3 border rounded-lg resize-none text-sm" placeholder="Escribe el contenido del email..." />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Archivos Adjuntos</label>
              <input type="file" multiple onChange={(e) => handleFileUpload(e, false)} className="text-sm" />
              {templateAttachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {templateAttachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span>{att.filename}</span>
                      <button onClick={() => removeAttachment(att.id, false)} className="text-red-500 hover:text-red-700">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTemplateModal(false)}>Cancelar</Button>
            <Button onClick={handleAddTemplate} className="bg-[#D4A843] hover:bg-[#c49a3a]">Crear Plantilla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Modal */}
      <Dialog open={showEditTemplateModal} onOpenChange={setShowEditTemplateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Plantilla</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Nombre</label>
                  <Input value={selectedTemplate.name} onChange={(e) => setSelectedTemplate({...selectedTemplate, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Categoría</label>
                  <Select value={selectedTemplate.category || ''} onValueChange={(v) => setSelectedTemplate({...selectedTemplate, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bienvenida">Bienvenida</SelectItem>
                      <SelectItem value="seguimiento">Seguimiento</SelectItem>
                      <SelectItem value="propuesta">Propuesta</SelectItem>
                      <SelectItem value="agradecimiento">Agradecimiento</SelectItem>
                      <SelectItem value="promocion">Promoción</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Asunto</label>
                <Input value={selectedTemplate.subject} onChange={(e) => setSelectedTemplate({...selectedTemplate, subject: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Variables disponibles</label>
                <div className="p-3 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
                  {Object.entries(tagsByCategory).map(([category, tags]) => (
                    <div key={category} className="mb-2">
                      <p className="text-xs font-medium text-gray-500 mb-1 capitalize">{category}</p>
                      <div className="flex flex-wrap gap-1">
                        {tags.map((t) => (
                          <button key={t.tag} onClick={() => insertTag(t.tag, true)} className="px-2 py-1 text-xs bg-white border rounded hover:bg-[#D4A843] hover:text-white transition-colors" title={t.label}>
                            <Tag size={10} className="inline mr-1" />{t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Cuerpo</label>
                <textarea id="edit-template-body" value={selectedTemplate.body} onChange={(e) => setSelectedTemplate({...selectedTemplate, body: e.target.value})} className="w-full h-40 p-3 border rounded-lg resize-none text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Archivos Adjuntos</label>
                <input type="file" multiple onChange={(e) => handleFileUpload(e, true)} className="text-sm" />
                {editAttachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {editAttachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span>{att.filename}</span>
                        <button onClick={() => removeAttachment(att.id, true)} className="text-red-500 hover:text-red-700">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTemplateModal(false)}>Cancelar</Button>
            <Button onClick={handleEditTemplate} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* AI Template Assistant Modal */}
      <Dialog open={showAIAssistant} onOpenChange={setShowAIAssistant}>
        <DialogContent className="max-w-2xl max-h-[min(850px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-500" />
              Asistente de Creación de Templates con AI
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {!generatedTemplate ? (
              <>
                {/* Template Type */}
                <div>
                  <Label className="text-base font-medium">Tipo de Template</Label>
                  <Select
                    value={assistantForm.template_type}
                    onValueChange={(val) => setAssistantForm(prev => ({ ...prev, template_type: val }))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bienvenida">Bienvenida</SelectItem>
                      <SelectItem value="seguimiento">Seguimiento</SelectItem>
                      <SelectItem value="propuesta">Propuesta</SelectItem>
                      <SelectItem value="agradecimiento">Agradecimiento</SelectItem>
                      <SelectItem value="promoción">Promoción</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Purpose */}
                <div>
                  <Label className="text-base font-medium">Propósito del Template *</Label>
                  <Textarea
                    value={assistantForm.purpose}
                    onChange={e => setAssistantForm(prev => ({ ...prev, purpose: e.target.value }))}
                    placeholder="Describe qué debe lograr este template. Ej: 'Enviar propuesta de servicios de producción de video para eventos corporativos con precios y paquetes disponibles'"
                    rows={3}
                    className="mt-2"
                  />
                </div>
                
                {/* Tone */}
                <div>
                  <Label className="text-base font-medium">Tono</Label>
                  <Select
                    value={assistantForm.tone}
                    onValueChange={(val) => setAssistantForm(prev => ({ ...prev, tone: val }))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="profesional">Profesional</SelectItem>
                      <SelectItem value="amigable">Amigable</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Target Audience */}
                <div>
                  <Label className="text-base font-medium">Audiencia Objetivo</Label>
                  <Input
                    value={assistantForm.target_audience}
                    onChange={e => setAssistantForm(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="Ej: Directores de marketing de empresas medianas"
                    className="mt-2"
                  />
                </div>
                
                {/* Key Points */}
                <div>
                  <Label className="text-base font-medium">Puntos Clave a Incluir</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newKeyPoint}
                      onChange={e => setNewKeyPoint(e.target.value)}
                      placeholder="Agregar punto clave..."
                      onKeyPress={e => e.key === 'Enter' && addKeyPoint()}
                    />
                    <Button type="button" onClick={addKeyPoint} variant="outline">
                      <Plus size={16} />
                    </Button>
                  </div>
                  {assistantForm.key_points.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {assistantForm.key_points.map((point, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {point}
                          <button onClick={() => removeKeyPoint(idx)} className="ml-1">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Generate Button */}
                <Button
                  onClick={handleGenerateTemplate}
                  disabled={generatingTemplate || !assistantForm.purpose.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {generatingTemplate ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Generando template con AI...
                    </>
                  ) : (
                    <>
                      <Wand2 size={16} className="mr-2" />
                      Generar Template
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                {/* Generated Template Preview */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">{generatedTemplate.name}</h4>
                  <Badge className="mb-3">{generatedTemplate.category}</Badge>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-purple-700">Asunto:</Label>
                      <Input
                        value={generatedTemplate.subject}
                        onChange={e => setGeneratedTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        className="mt-1 bg-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm text-purple-700">Contenido:</Label>
                      <Textarea
                        value={generatedTemplate.body}
                        onChange={e => setGeneratedTemplate(prev => ({ ...prev, body: e.target.value }))}
                        rows={10}
                        className="mt-1 bg-white font-mono text-sm"
                      />
                    </div>
                    
                    {generatedTemplate.variables?.length > 0 && (
                      <div>
                        <Label className="text-sm text-purple-700">Variables usadas:</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {generatedTemplate.variables.map((v, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {v}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {generatedTemplate.tips?.length > 0 && (
                      <div className="mt-3 p-2 bg-white rounded border">
                        <Label className="text-sm text-purple-700 flex items-center gap-1">
                          <AlertCircle size={14} />
                          Tips para usar este template:
                        </Label>
                        <ul className="text-xs text-gray-600 mt-1 list-disc list-inside">
                          {generatedTemplate.tips.map((tip, idx) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => setGeneratedTemplate(null)}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Generar otro
                  </Button>
                  <Button
                    onClick={handleSaveGeneratedTemplate}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Save size={16} className="mr-2" />
                    Guardar Template
                  </Button>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowAIAssistant(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Message Template Modal */}
      <Dialog open={showAddMessageModal} onOpenChange={setShowAddMessageModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="text-green-500" />
              Nueva Plantilla de Mensaje
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la plantilla</Label>
              <Input 
                value={newMessage.name} 
                onChange={(e) => setNewMessage({...newMessage, name: e.target.value})} 
                placeholder="Ej: Saludo inicial WhatsApp" 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Canal</Label>
                <Select value={newMessage.channel} onValueChange={(v) => setNewMessage({...newMessage, channel: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría</Label>
                <Select value={newMessage.category} onValueChange={(v) => setNewMessage({...newMessage, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saludo">Saludo</SelectItem>
                    <SelectItem value="seguimiento">Seguimiento</SelectItem>
                    <SelectItem value="propuesta">Propuesta</SelectItem>
                    <SelectItem value="confirmacion">Confirmación</SelectItem>
                    <SelectItem value="agradecimiento">Agradecimiento</SelectItem>
                    <SelectItem value="recordatorio">Recordatorio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Variables disponibles</Label>
              <div className="flex flex-wrap gap-1 p-2 bg-gray-50 rounded">
                {messageVars.map((v) => (
                  <button 
                    key={v.tag} 
                    onClick={() => insertMessageTag(v.tag)}
                    className="px-2 py-1 text-xs bg-white border rounded hover:bg-green-50 hover:border-green-300 transition-colors"
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Contenido del mensaje</Label>
              <Textarea 
                id="message-content"
                value={newMessage.content} 
                onChange={(e) => setNewMessage({...newMessage, content: e.target.value})} 
                placeholder="Escribe tu mensaje aquí. Usa variables como {{nombre_contacto}} para personalizar."
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMessageModal(false)}>Cancelar</Button>
            <Button onClick={handleAddMessage} className="bg-green-600 hover:bg-green-700">Crear Plantilla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Message Template Modal */}
      <Dialog open={showEditMessageModal} onOpenChange={setShowEditMessageModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="text-gray-500" />
              Editar Plantilla de Mensaje
            </DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div>
                <Label>Nombre de la plantilla</Label>
                <Input 
                  value={selectedMessage.name} 
                  onChange={(e) => setSelectedMessage({...selectedMessage, name: e.target.value})} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Canal</Label>
                  <Select value={selectedMessage.channel || 'whatsapp'} onValueChange={(v) => setSelectedMessage({...selectedMessage, channel: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={selectedMessage.category || ''} onValueChange={(v) => setSelectedMessage({...selectedMessage, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="saludo">Saludo</SelectItem>
                      <SelectItem value="seguimiento">Seguimiento</SelectItem>
                      <SelectItem value="propuesta">Propuesta</SelectItem>
                      <SelectItem value="confirmacion">Confirmación</SelectItem>
                      <SelectItem value="agradecimiento">Agradecimiento</SelectItem>
                      <SelectItem value="recordatorio">Recordatorio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Variables disponibles</Label>
                <div className="flex flex-wrap gap-1 p-2 bg-gray-50 rounded">
                  {messageVars.map((v) => (
                    <button 
                      key={v.tag} 
                      onClick={() => insertMessageTag(v.tag)}
                      className="px-2 py-1 text-xs bg-white border rounded hover:bg-green-50 hover:border-green-300 transition-colors"
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Contenido del mensaje</Label>
                <Textarea 
                  id="message-content"
                  value={selectedMessage.content} 
                  onChange={(e) => setSelectedMessage({...selectedMessage, content: e.target.value})} 
                  rows={6}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMessageModal(false)}>Cancelar</Button>
            <Button onClick={handleEditMessage} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TemplatesTab;
